/**
 * Scheduled Reports API service functions
 * Handles CRUD operations for scheduled reports (dashboards_scheduled table)
 */
import { buildConfigApiUrl } from '@api/api';

const getHeaders = () => ({
  'Content-Type': 'application/json',
});

/**
 * Create a new scheduled report
 * @param {Object} scheduledReportData - The scheduled report data
 * @param {string} scheduledReportData.name - Name of the scheduled report
 * @param {string} scheduledReportData.description - Description of the scheduled report
 * @param {number[]} scheduledReportData.savedReportIds - IDs of saved reports to include
 * @param {string} scheduledReportData.outputFormat - Output format (pdf or excel)
 * @param {string} scheduledReportData.scheduleType - Schedule type (daily, weekly, monthly)
 * @param {string} scheduledReportData.scheduleTime - Time of day (HH:MM)
 * @param {string} scheduledReportData.scheduleDays - Days to run (comma-separated)
 * @param {string[]} scheduledReportData.recipients - Email recipients
 */
export const createScheduledReport = async (scheduledReportData) => {
  try {
    const response = await fetch(buildConfigApiUrl('/scheduled_reports'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        name: scheduledReportData.name,
        description: scheduledReportData.description || '',
        savedReportIds: scheduledReportData.savedReportIds || [],
        outputFormat: scheduledReportData.outputFormat || 'pdf',
        scheduleType: scheduledReportData.scheduleType || 'daily',
        scheduleTime: scheduledReportData.scheduleTime || '09:00',
        scheduleDays: scheduledReportData.scheduleDays || null,
        startDate: scheduledReportData.startDate || null,
        endDate: scheduledReportData.endDate || null,
        recipients: scheduledReportData.recipients || [],
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData.message || `HTTP error! status: ${response.status}`;
      throw new Error(Array.isArray(message) ? message[0] : message);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Fetch all scheduled reports
 */
export const fetchScheduledReports = async () => {
  try {
    const response = await fetch(buildConfigApiUrl('/scheduled_reports'), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: `Failed to load scheduled reports: ${error.message}`,
      data: [],
    };
  }
};

/**
 * Fetch a specific scheduled report by ID
 */
export const fetchScheduledReportById = async (id) => {
  try {
    const response = await fetch(buildConfigApiUrl(`/scheduled_reports/${id}`), {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Scheduled report not found');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Update an existing scheduled report
 */
export const updateScheduledReport = async (id, scheduledReportData) => {
  try {
    const body = {};

    if (scheduledReportData.name !== undefined) body.name = scheduledReportData.name;
    if (scheduledReportData.description !== undefined) body.description = scheduledReportData.description;
    if (scheduledReportData.status !== undefined) body.status = scheduledReportData.status;
    if (scheduledReportData.savedReportIds !== undefined) body.savedReportIds = scheduledReportData.savedReportIds;
    if (scheduledReportData.outputFormat !== undefined) body.outputFormat = scheduledReportData.outputFormat;
    if (scheduledReportData.scheduleType !== undefined) body.scheduleType = scheduledReportData.scheduleType;
    if (scheduledReportData.scheduleTime !== undefined) body.scheduleTime = scheduledReportData.scheduleTime;
    if (scheduledReportData.scheduleDays !== undefined) body.scheduleDays = scheduledReportData.scheduleDays;
    if (scheduledReportData.startDate !== undefined) body.startDate = scheduledReportData.startDate;
    if (scheduledReportData.endDate !== undefined) body.endDate = scheduledReportData.endDate;
    if (scheduledReportData.recipients !== undefined) body.recipients = scheduledReportData.recipients;

    const response = await fetch(buildConfigApiUrl(`/scheduled_reports/${id}`), {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Scheduled report not found');
      }
      if (response.status === 409) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'A scheduled report with this name already exists');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Delete a scheduled report
 */
export const deleteScheduledReport = async (id) => {
  try {
    const response = await fetch(buildConfigApiUrl(`/scheduled_reports/${id}`), {
      method: 'DELETE',
      headers: getHeaders(),
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Scheduled report not found');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return {
      status: 'success',
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Search scheduled reports with pagination, filtering, and sorting
 */
export const searchScheduledReports = async ({
  page = 1,
  pageSize = 50,
  sortField = 'createdAt',
  sortOrder = 'desc',
  filters = [],
  searchTerm = '',
  dashboardId = null,
}) => {
  try {
    const body = {
      page,
      pageSize,
      sortField,
      sortOrder,
      filters,
      searchTerm,
    };

    // Only include dashboardId if provided
    if (dashboardId !== null) {
      body.dashboardId = dashboardId;
    }

    const response = await fetch(buildConfigApiUrl('/scheduled_reports/search'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      rows: data.data || [],
      total: data.total || 0,
    };
  } catch {
    return {
      rows: [],
      total: 0,
    };
  }
};

/**
 * Bulk delete scheduled reports
 */
export const bulkDeleteScheduledReports = async ({ ids }) => {
  try {
    const response = await fetch(buildConfigApiUrl('/scheduled_reports/bulk/delete'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ ids }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return {
      status: 'success',
      data,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
    };
  }
};

/**
 * Toggle scheduled report status (enable/disable)
 */
export const toggleScheduledReportStatus = async (id, status) => updateScheduledReport(id, { status });

/**
 * Fetch scheduled reports filtered by dashboard ID
 * @param {number} dashboardId - The dashboard ID to filter by
 */
export const fetchScheduledReportsByDashboard = async (dashboardId) => {
  try {
    const result = await searchScheduledReports({
      dashboardId,
      pageSize: 100, // Get all for the modal
      sortField: 'createdAt',
      sortOrder: 'desc',
    });

    return {
      status: 'success',
      data: result.rows || [],
      total: result.total || 0,
    };
  } catch (error) {
    return {
      status: 'error',
      message: error.message,
      data: [],
    };
  }
};

/**
 * Helper to safely map array values to dropdown format
 * @param {Array} values - Array of values from API
 * @returns {Array} - Array of dropdown options
 */
const mapToDropdownOptions = (values) => {
  if (!Array.isArray(values)) return [];
  return values
    .filter((value) => value != null && value !== '')
    .map((value) => ({
      value: String(value),
      label: String(value),
    }));
};

/**
 * Maps API response to dropdown format for scheduled reports filters
 * @param {Object} apiResponse - Response from the API
 * @returns {Object} - Mapped filter options in dropdown format
 */
const mapFilterOptionsToDropdownFormat = (apiResponse) => {
  if (!apiResponse) {
    return {};
  }

  const result = {};

  if (apiResponse.name) {
    result.name = mapToDropdownOptions(apiResponse.name);
  }

  if (apiResponse.description) {
    result.description = mapToDropdownOptions(apiResponse.description);
  }

  if (apiResponse.dashboardName) {
    result.dashboardName = mapToDropdownOptions(apiResponse.dashboardName);
  }

  if (apiResponse.scheduleType) {
    result.scheduleType = mapToDropdownOptions(apiResponse.scheduleType);
  }

  if (apiResponse.outputFormat) {
    result.outputFormat = mapToDropdownOptions(apiResponse.outputFormat);
  }

  return result;
};

/**
 * Converts UI filters array to API format for scheduled reports
 * @param {Array} uiFilters - Array of filter objects from UI { type, value }
 * @returns {Array} - Filters in API format
 */
export const convertScheduledReportFiltersToApiFormat = (uiFilters = []) => {
  const apiFilters = [];

  uiFilters.forEach((filter) => {
    if (!filter.type || !filter.value || filter.value.length === 0) return;

    apiFilters.push({
      type: filter.type,
      value: Array.isArray(filter.value) ? filter.value[0] : filter.value,
    });
  });

  return apiFilters;
};

/**
 * Fetches available filter values for scheduled reports search
 * @param {Array} currentFilters - Current filters to apply when fetching options
 * @param {string} searchTerm - Optional search term to filter options
 * @returns {Promise<Object>} - Object with filter options for each filter type
 */
export const getScheduledReportFilterOptions = async (
  currentFilters = [],
  searchTerm = '',
) => {
  try {
    const requestBody = {
      filters: convertScheduledReportFiltersToApiFormat(currentFilters),
    };
    if (searchTerm) {
      requestBody.searchTerm = searchTerm;
    }

    const response = await fetch(buildConfigApiUrl('/scheduled_reports/search/filters'), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error('Failed to fetch filter options');
    }

    const data = await response.json();
    return mapFilterOptionsToDropdownFormat(data);
  } catch (error) {
    console.error('Error fetching scheduled report filter options:', error);
    throw error;
  }
};

